"use client";
import CustomerConstants from "@/common/constants/customer";
import Button from "@/components/ui/Button";
import useTranslationApi from "@/lib/hooks/useTranslationApi";
import { setPopupId } from "@/redux/features/ShopSlice";
import { useAppDispatch, useAppSelector } from "@/redux/hook";
import { usePathname, useRouter } from "next/navigation";
import React, { useCallback, useEffect, useState } from "react";

type Props = {
	page: string;
};

type authFooter = {
	title: string;
	description: string[];
	button: {
		title: string;
		link: string;
	};
};

const AuthFooter = () => {
	const { t } = useTranslationApi([
		"authfooter_register_desc_1",
		"authfooter_register_desc_2",
		"authfooter_register_title",
		"authfooter_login_desc1",
		"authfooter_login_desc2",
		"register_heading",
		"orderstatus_heading",
		"orderstatus_title",
		"authfooter_login_title",
		"orderstatus_desc1",
		"orderstatus_desc2",
		"login_heading",
	]);
	const router = useRouter();
	const [authFooters, setAuthFooters] = useState<authFooter[]>([]);
	const [page, setPage] = useState<string>("");
	const pathname = usePathname();
	const dispatch = useAppDispatch();

	const getAuthFooters = useCallback(() => {
		switch (page) {
			case "register":
				return [
					{
						title: "",
						description: [],
						button: {
							title: "",
							link: "",
						},
					},
					{
						title: t("authfooter_register_title"),
						description: [
							t("authfooter_register_desc_1"),
							t("authfooter_register_desc_2"),
						],
						button: {
							title: t("login_heading"),
							link: CustomerConstants.LINK_ROUTER.LOGIN.LOGIN,
						},
					},
				];
			case "login":
				return [
					{
						title: t("authfooter_login_title"),
						description: [
							t("authfooter_login_desc1"),
							t("authfooter_login_desc2"),
						],
						button: {
							title: t("register_heading"),
							link: CustomerConstants.LINK_ROUTER.LOGIN.REGISTER,
						},
					},
					{
						title: t("orderstatus_heading"),
						description: [t("orderstatus_desc1"), t("orderstatus_desc2")],
						button: { title: t("orderstatus_title"), link: "/orderstatus" },
					},
				];
			case "forgotPassword":
				return [
					{
						title: t("authfooter_login_title"),
						description: [
							t("authfooter_login_desc1"),
							t("authfooter_login_desc2"),
						],
						button: {
							title: t("register_heading"),
							link: CustomerConstants.LINK_ROUTER.LOGIN.REGISTER,
						},
					},
					{
						title: t("orderstatus_heading"),
						description: [t("orderstatus_desc1"), t("orderstatus_desc2")],
						button: { title: t("orderstatus_title"), link: "" },
					},
				];
			case "resetPassword":
				return [
					{
						title: t("authfooter_login_title"),
						description: [
							t("authfooter_register_desc_1"),
							t("authfooter_register_desc_2"),
						],
						button: {
							title: t("register_heading"),
							link: CustomerConstants.LINK_ROUTER.LOGIN.REGISTER,
						},
					},
					{
						title: t("authfooter_register_title"),
						description: [
							t("authfooter_register_desc_1"),
							t("authfooter_register_desc_2"),
						],
						button: {
							title: t("login_heading"),
							link: CustomerConstants.LINK_ROUTER.LOGIN.LOGIN,
						},
					},
				];
			case "verifyOTP":
				return [
					{
						title: t("authfooter_login_title"),
						description: [
							t("authfooter_login_desc1"),
							t("authfooter_login_desc2"),
						],
						button: { title: t("register_heading"), link: "/account/register" },
					},
					{
						title: t("authfooter_register_title"),
						description: [
							t("authfooter_register_desc_1"),
							t("authfooter_register_desc_2"),
						],
						button: {
							title: t("login_heading"),
							link: CustomerConstants.LINK_ROUTER.LOGIN.LOGIN,
						},
					},
				];
			case "updateCustomer":
				return [
					{
						title: t("authfooter_login_title"),
						description: [
							t("authfooter_login_desc1"),
							t("authfooter_login_desc2"),
						],
						button: {
							title: t("register_heading"),
							link: CustomerConstants.LINK_ROUTER.LOGIN.REGISTER,
						},
					},
					{
						title: t("authfooter_register_title"),
						description: [
							t("authfooter_register_desc_1"),
							t("authfooter_register_desc_2"),
						],
						button: {
							title: t("login_heading"),
							link: CustomerConstants.LINK_ROUTER.LOGIN.LOGIN,
						},
					},
				];
			default:
				return [];
		}
	}, [page, t, CustomerConstants]);

	useEffect(() => {
		if (pathname === "/account/register") {
			setPage("register");
		} else if (
			location.pathname === CustomerConstants.LINK_ROUTER.LOGIN.LOGIN
		) {
			setPage("login");
		} else if (
			location.pathname === CustomerConstants.LINK_ROUTER.LOGIN.FORGOT_PASSWORD
		) {
			setPage("forgotPassword");
		} else if (
			location.pathname === CustomerConstants.LINK_ROUTER.LOGIN.RESET_PASSWORD
		) {
			setPage("resetPassword");
		} else if (
			location.pathname === CustomerConstants.LINK_ROUTER.LOGIN.VERIFY_OTP ||
			location.pathname === CustomerConstants.LINK_ROUTER.LOGIN.VERIFY_OTP_QUICK
		) {
			setPage("verifyOTP");
		} else if (location.pathname.includes("/updatecustomer")) {
			setPage("updateCustomer");
		}
	}, [pathname]);

	return (
		<div className="flex justify-center items-center flex-col bg-gray-300">
			{getAuthFooters().map(
				(footer, index) =>
					footer.title !== "" && (
						<div
							key={index}
							className="w-full h-[198px] flex justify-center items-center border-t border-gray-400 text-center flex-col py-0 px-4 md:px-0">
							{/* <div className="auth_footer_heading" style={{ marginBottom: 8 }}> */}
							<div className="mt-[40px] mb-2">
								<h4 className="text-gray-500 text-h4 font-bold">
									{footer.title}
								</h4>
							</div>
							<div className="mt-1">
								{footer.description.map((item, index) => (
									<p key={index} className="text-sm">
										{item}
									</p>
								))}
							</div>
							<div className="mt-6 mb-[40px] w-full">
								<Button
									className="w-full max-w-[396px] mx-auto"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										if (footer.button.link.length > 0) {
											if (footer.button.link === "/orderstatus") {
												dispatch(setPopupId("popup-order-status"));
												return;
											}
											router.push(footer.button.link);
										}
									}}
									label={footer.button.title}
									mode="light"></Button>
							</div>
						</div>
					)
			)}
		</div>
	);
};

export default AuthFooter;
